/**
 * CustomerPortalStopMetaStrip — customer-portal multi-stop route summary on the Quote Detail
 * Overview tab. Ported "same to same" from WALHQ's StopSummaryStrip.tsx (wal-frontend-v2,
 * QuotingHub/QuotingDashboard/Quote/QuoteDetailPage/Quotev2/ShoppingLanes/StopSummaryStrip.tsx) —
 * same table-metadata-heading/value-text classes, same border-right-between-segments layout, same
 * two-tooltip split (stop name hover -> services list; each up/down count hover -> its own item
 * breakdown table), same green-up/red-down count colors. Replaces CustomerPortalStopCards.tsx's
 * card grid (kept, not deleted, per this section's established convention of preserving prior
 * design iterations rather than removing them).
 *
 * Bookend (Shipper/Consignee) services are quote-scoped (stopId null) — never on a stop's own
 * serviceIds — resolved from quotingDetails.selectedServices, split by service.type (PICKUP for
 * Shipper, DELIVERY for Consignee). Middle-stop services stay serviceIds-driven, but are ALSO
 * split by the service catalog's own type (bug fix, user-reported) — a middle stop hinted "Both"
 * can carry pickup and delivery accessorials at once, and the tooltip shows them as two separate
 * labeled sections instead of merging them into one flat list.
 */
import { ArrowDown, ArrowUp, Check } from '@untitled-ui/icons-react/build/cjs';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import TableCmp from 'src/components/TableCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import { RootState } from 'src/redux/store';
import { getFormattedNumber } from 'src/utils/CommonFunctions';

interface IMetaStripStop {
    id: number;
    position: number;
    city: string | null;
    state?: string | null;
    stateCode?: string | null;
    companyName?: string | null;
    fullAddress?: string | null;
    serviceIds?: unknown;
    // Resolved per-stop services (name + type) attached by the backend's attachMultiStopFields.
    // Preferred over serviceIds + the Redux service catalog — it's the only source available on
    // the public share-link page, which cannot make authenticated catalog requests.
    services?: { name?: string | null; type?: string | null }[] | null;
}

interface IStopSummaryCount {
    position: number;
    unitsOn: number;
    unitsOff: number;
}

interface IDimension {
    id: number;
    pickupStopId?: number | null;
    dropStopId?: number | null;
    handlingUnit?: number | string | null;
    handlingUnitDetails?: { name?: string | null } | null;
    handlingUnitNo?: number | null;
    itemLength?: number | null;
    width?: number | null;
    height?: number | null;
    totalWeight?: number | null;
    weightMeasurement?: string | null;
    lengthMeasurement?: string | null;
    freightClass?: string | number | null;
    commodity?: string | null;
    description?: string | null;
}

interface ISelectedService {
    serviceId?: number | null;
    service?: { name?: string | null; type?: string | null } | null;
}

interface IStopServiceView {
    name: string;
}

interface IServiceCatalogEntry {
    name: string;
    type?: string | null;
}

interface IProps {
    stops: IMetaStripStop[];
    summary?: { stops: IStopSummaryCount[] };
    dimensions?: IDimension[];
    // Quote-scoped services (quotingDetails.selectedServices) — the ONLY source for the
    // bookends' own services (see file header).
    selectedServices?: ISelectedService[];
    isLoading?: boolean;
    skeletonStopCount?: number;
}

export interface IStopCard {
    position: number;
    label: string;
    stopName: string;
    companyName: string;
    fullAddress: string;
    unitsOn: number;
    unitsOff: number;
    pickupItems: IDimension[];
    dropItems: IDimension[];
    // Bug fix (user-reported) — a middle stop hinted "Both" can carry pickup services AND delivery
    // services at once (e.g. Tailgate Pickup + Limited Access Delivery on the same stop). Split by
    // type instead of one flat list, so the tooltip below can show them as two separate sections
    // rather than merging pickup and delivery accessorials together.
    pickupServices: IStopServiceView[];
    deliveryServices: IStopServiceView[];
}

/** Row shape of the pickup/drop-off hover table — Handling Unit / № / dims / weight / freight class,
 * the column order Figma specifies (WAL-HQ-V2 Quoting, "Line Items hover d…"), which is also the order
 * StopDetailItemRow below already uses. The Handling Unit type name resolves the same way it does
 * there: the backend-attached handlingUnitDetails first, the Redux catalog only as a fallback (the
 * public share-link page cannot fetch the catalog, so the attached name is the source there). */
const StopItemRow = ({ data, handlingUnitOptions }: { data: IDimension; handlingUnitOptions?: any[] }) => {
    const { t } = useTranslation();
    const lengthUnit = data.lengthMeasurement === 'CM' ? t('MyQuote.Unit_Cm') : t('MyQuote.Unit_In');
    const weightUnit = data.weightMeasurement === 'KGS' ? t('MyQuote.Unit_Kgs') : t('MyQuote.Unit_Lbs');
    const hasDims = data.itemLength && data.width && data.height;
    const handlingUnitLabel = data.handlingUnitDetails?.name || handlingUnitOptions?.find((o: any) => o.value === Number(data.handlingUnit))?.label || '-';

    return (
        <>
            <td className="w-[26%]">{handlingUnitLabel}</td>
            <td className="w-[12%]">{data.handlingUnitNo ?? '-'}</td>
            <td className="w-[26%]">{hasDims ? `${data.itemLength}x${data.width}x${data.height} ${lengthUnit}` : '-'}</td>
            <td className="w-[22%]">{data.totalWeight ? `${getFormattedNumber(data.totalWeight, false)} ${weightUnit}` : '-'}</td>
            <td className="w-[14%]">{data.freightClass ?? '-'}</td>
        </>
    );
};

/** The hover popover for a stop's pickup/drop count. Reads the handling-unit catalog straight from
 * Redux (not via useAddressDetails()) for the same reason the strip itself does — this also renders
 * on the public share-link page, where an authenticated fetch would 401 the visitor out. */
const StopItemsTooltipContent: FC<{ title: string; items: IDimension[] }> = ({ title, items }) => {
    const { t } = useTranslation();
    const handlingUnitOptions = useSelector((state: RootState) => state.basic.handlingUnitOptions);

    return (
        <>
            <p className="text-xs text-grayLight900 font-medium text-left mb-1.5">{title}</p>
            <div className="tooltip-content-with-scroll pb-1">
                <div className="table-box-wrap">
                    <div className="table-box-container overflow-hidden">
                        <div className="overflow-x-auto custom-scrollbar-v2 scrollbar-hide table-without-title-header table-without-pagination">
                            <TableCmp
                                tableRowHeight="xs"
                                headCells={[
                                    { id: 'handlingUnit', name: t('Common.Handling_Unit'), visible: true },
                                    { id: 'handlingUnitNo', name: t('QuotingHub.QuotingDashboard.No_Symbol'), visible: true },
                                    { id: 'dimensions', name: t('Common.Dimensions'), visible: true },
                                    { id: 'totalWeight', name: t('Common.Total_Weight'), visible: true },
                                    { id: 'freightClass', name: t('Common.FC'), visible: true },
                                ]}
                                tableDataArr={items}
                                TableRowCmp={StopItemRow}
                                tableRowCmpProps={{ handlingUnitOptions }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

/** One labeled bullet section — used standalone (single-type stop) or twice (a "Both" middle stop). */
const StopServicesSection: FC<{ title: string; services: IStopServiceView[] }> = ({ title, services }) => (
    <div className="space-y-1">
        <p className="text-xs font-semibold text-grayLight900">{title}</p>
        <ul className="list-disc pl-4 space-y-1">
            {services.map((svc, idx) => (
                <li key={idx} className="text-xs text-grayLight600">
                    {svc.name}
                </li>
            ))}
        </ul>
    </div>
);

/** Row for the Stop Details modal's item tables — adds the Handling Unit type name (e.g. "Pallet")
 * as its own leading column, which the hover-tooltip table (StopItemRow above) doesn't show. */
const StopDetailItemRow = ({ data, handlingUnitOptions }: { data: IDimension; handlingUnitOptions?: any[] }) => {
    const { t } = useTranslation();
    const lengthUnit = data.lengthMeasurement === 'CM' ? t('MyQuote.Unit_Cm') : t('MyQuote.Unit_In');
    const weightUnit = data.weightMeasurement === 'KGS' ? t('MyQuote.Unit_Kgs') : t('MyQuote.Unit_Lbs');
    const hasDims = data.itemLength && data.width && data.height;
    const handlingUnitLabel = data.handlingUnitDetails?.name || handlingUnitOptions?.find((o: any) => o.value === Number(data.handlingUnit))?.label || '-';

    return (
        <>
            <td className="w-[26%]">{handlingUnitLabel}</td>
            <td className="w-[12%]">{data.handlingUnitNo ?? '-'}</td>
            <td className="w-[26%]">{hasDims ? `${data.itemLength}x${data.width}x${data.height} ${lengthUnit}` : '-'}</td>
            <td className="w-[22%]">{data.totalWeight ? `${getFormattedNumber(data.totalWeight, false)} ${weightUnit}` : '-'}</td>
            <td className="w-[14%]">{data.freightClass ?? '-'}</td>
        </>
    );
};

/** Pixel-matches the Stop Details modal's "Pickup/Delivery Additional Services" section (Figma:
 * WAL-HQ-V2 Quoting, node 71784:279556 — label above a bg-utilityGray300/border-utilityGray200
 * rounded box, each service an equal-width flex column of check icon + text-grayLight600 label).
 * Renders full width, one section per direction stacked vertically — NOT side by side. Distinct
 * from StopServicesSection's bulleted list, which stays as-is for the strip's hover tooltip. */
const StopDetailServicesColumn: FC<{ title: string; services: IStopServiceView[] }> = ({ title, services }) => (
    <div className="flex flex-col gap-1.5 w-full">
        <p className="text-xs leading-[18px] font-medium text-textSecondary">{title}</p>
        <div className="gap-1.5 p-3 rounded-lg grid grid-cols-2 border border-utilityGray200 bg-utilityGray300">
            {services.map((svc, idx) => (
                <div key={idx} className="flex flex-1 min-w-0 items-center gap-1.5">
                    <Check className="w-4 h-4 text-gray500 flex-none" />
                    <p className="text-xs leading-[18px] text-grayLight600 truncate text-nowrap">{svc.name}</p>
                </div>
            ))}
        </div>
    </div>
);

/** One numbered stop's full block inside the Edit Quote modal's "Pickups & Drop-offs" tab (see
 * MyQuote/Modals/PickupsDropoffsDetails.tsx) — pixel-matches Figma (WAL-HQ-V2 Quoting, node
 * 71696:503245): numbered circle + address, then (when
 * present) the Stop Pickups table, the Stop Dropoffs table, and the additional-services columns —
 * side by side only when both directions have services (a "Both" middle stop), full width otherwise. */
export const StopDetailBlock: FC<{ index: number; card: IStopCard; handlingUnitOptions?: any[] }> = ({ index, card, handlingUnitOptions }) => {
    const { t } = useTranslation();
    const hasPickupItems = card.pickupItems.length > 0;
    const hasDropItems = card.dropItems.length > 0;
    const hasPickupServices = card.pickupServices.length > 0;
    const hasDeliveryServices = card.deliveryServices.length > 0;
    const hasAnyDetail = hasPickupItems || hasDropItems || hasPickupServices || hasDeliveryServices;

    // Deliberately NOT table-box-wrap/table-without-title-header (the shared full-table classes) —
    // those add a shadow-md + rounded-xl (too heavy for this inline mini table) and, worse,
    // `.table-without-title-header thead th` forces its own bg-gray50 straight onto each <th>,
    // which paints over (and defeats) any tableHeaderClass override on the <thead>. This wrapper
    // pixel-matches Figma directly instead: a plain 1px border, 6px radius, no shadow.
    const renderTable = (items: IDimension[]) => (
        <div className="rounded-md border border-utilityGray200 bg-white overflow-hidden">
            <div className="overflow-x-auto custom-scrollbar-v2 scrollbar-hide">
                <TableCmp
                    tableRowHeight="xs"
                    headCells={[
                        { id: 'handlingUnit', name: t('Common.Handling_Unit'), visible: true },
                        { id: 'handlingUnitNo', name: t('QuotingHub.QuotingDashboard.No_Symbol'), visible: true },
                        { id: 'dimensions', name: t('Common.Dimensions'), visible: true },
                        { id: 'totalWeight', name: t('Common.Total_Weight'), visible: true },
                        { id: 'freightClass', name: t('Common.FC'), visible: true },
                    ]}
                    tableDataArr={items}
                    tableBodyRowClass="last:border-b-0"
                    TableRowCmp={StopDetailItemRow}
                    tableRowCmpProps={{ handlingUnitOptions }}
                />
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-md bg-white border-[0.5px] border-black/[0.08] flex items-center justify-center flex-none">
                    <span className="text-xs font-semibold text-grayLight600">{index}</span>
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-medium text-grayLight900 truncate">{card.stopName || '-'}</p>
                    <p className="text-xs text-textSecondary truncate leading-[18px]">{card.fullAddress || '-'}</p>
                </div>
            </div>

            {hasAnyDetail && (
                <>
                    {/* Divider between the address header and the pickup/dropoff content — indented
                    to align with the address text (avatar w-6 + gap-2 = 32px = ml-8), matches Figma. */}
                    <div className="h-px bg-utilityGray200 ml-8" />
                    <div className="flex flex-col gap-4 pl-8">
                        {hasPickupItems && (
                            <div className="space-y-1.5">
                                <p className="text-xs font-medium text-grayLight600 leading-[18px]">{t('MyQuote.Stop_Pickups')}</p>
                                {renderTable(card.pickupItems)}
                            </div>
                        )}

                        {hasDropItems && (
                            <div className="space-y-1.5">
                                <p className="text-xs font-medium text-grayLight600 leading-[18px]">{t('MyQuote.Stop_Dropoffs')}</p>
                                {renderTable(card.dropItems)}
                            </div>
                        )}

                        {hasPickupServices && <StopDetailServicesColumn title={t('QuotingHub.Common.Pickup_Additional_Services')} services={card.pickupServices} />}
                        {hasDeliveryServices && <StopDetailServicesColumn title={t('QuotingHub.Common.Delivery_Additional_Services')} services={card.deliveryServices} />}
                    </div>
                </>
            )}
        </div>
    );
};

/** Pure card-builder shared by the strip below and by the Edit Quote modal's "Pickups & Drop-offs"
 * tab (MyQuote/Modals/PickupsDropoffsDetails.tsx), so this can't stay a private computation inside
 * the strip component. `t` is threaded in rather than called via useTranslation() so this stays a
 * plain function, callable from either a component body or a memoized value. */
export const buildStopCards = ({
    stops,
    summary,
    selectedServices = [],
    dimensions = [],
    services = [],
    t,
}: {
    stops: IMetaStripStop[];
    summary?: { stops: IStopSummaryCount[] };
    selectedServices?: ISelectedService[];
    dimensions?: IDimension[];
    services?: IServiceCatalogEntry[];
    t: TFunction;
}): IStopCard[] => {
    if (!stops || stops.length === 0) return [];

    const summaryByPosition = new Map((summary?.stops || []).map((s) => [s.position, s]));
    const serviceById = new Map<string, IServiceCatalogEntry>((services || []).map((service: any) => [String(service.id), { name: service.name, type: service.type }]));

    // Bookend services are quote-scoped (stopId null) — never on a stop's own serviceIds — so
    // they're resolved once here from quotingDetails.selectedServices, split by service.type.
    const shipperServiceNames = selectedServices.filter((s) => s.service?.type === 'PICKUP' && s.service?.name).map((s) => s.service!.name as string);
    const consigneeServiceNames = selectedServices.filter((s) => s.service?.type === 'DELIVERY' && s.service?.name).map((s) => s.service!.name as string);

    const sortedStops = [...stops].sort((a, b) => a.position - b.position);
    const firstPosition = sortedStops[0].position;
    const lastPosition = sortedStops[sortedStops.length - 1].position;

    return sortedStops.map((stop) => {
        const isFirst = stop.position === firstPosition;
        const isLast = stop.position === lastPosition && !isFirst;
        // Bookends read Shipper / Consignee; middles number 1..M relative to the shipper
        // ("Stop 1" is the first INTERMEDIATE stop, not the shipper) — matches StopSummaryStrip.tsx.
        const label = isFirst ? t('Common.Shipper') : isLast ? t('Common.Consignee') : t('MyQuote.Stop_N', { n: stop.position - firstPosition });

        // Bug fix (user-reported) — a middle stop hinted "Both" can carry pickup AND delivery
        // services at once; split by the service catalog's own `type` (same PICKUP/DELIVERY split
        // the bookends already use above) instead of dumping every serviceId into one flat list.
        let pickupServiceNames: string[];
        let deliveryServiceNames: string[];

        if (isFirst) {
            pickupServiceNames = shipperServiceNames;
            deliveryServiceNames = [];
        } else if (isLast) {
            pickupServiceNames = [];
            deliveryServiceNames = consigneeServiceNames;
        } else {
            // The backend attaches resolved { name, type } per stop — use it when present (the only
            // option on the public share-link page, which has no service catalog in Redux). Falls
            // back to serviceIds + catalog for stop payloads that carry ids only (e.g. the array
            // returned by the edit-quote save, which doesn't include resolved services).
            const stopServices = Array.isArray(stop.services)
                ? stop.services.filter((svc): svc is IServiceCatalogEntry => !!svc?.name)
                : (Array.isArray(stop.serviceIds) ? (stop.serviceIds as (string | number)[]) : []).map((id) => serviceById.get(String(id))).filter((svc): svc is IServiceCatalogEntry => !!svc);

            pickupServiceNames = stopServices.filter((svc) => svc.type === 'PICKUP').map((svc) => svc.name);
            deliveryServiceNames = stopServices.filter((svc) => svc.type === 'DELIVERY').map((svc) => svc.name);
        }

        const stopSummary = summaryByPosition.get(stop.position);

        return {
            position: stop.position,
            label,
            stopName: [stop.city, stop.state || stop.stateCode].filter(Boolean).join(', '),
            companyName: stop.companyName || '',
            fullAddress: stop.fullAddress || '',
            unitsOn: stopSummary?.unitsOn ?? 0,
            unitsOff: stopSummary?.unitsOff ?? 0,
            pickupItems: dimensions.filter((dim) => dim.pickupStopId === stop.id),
            dropItems: dimensions.filter((dim) => dim.dropStopId === stop.id),
            pickupServices: pickupServiceNames.map((name) => ({ name })),
            deliveryServices: deliveryServiceNames.map((name) => ({ name })),
        };
    });
};

/** Mirrors StopServicesTooltipContent.tsx's hover popover for a stop's name — bug fix (user-reported):
 * a middle stop hinted "Both" shows pickup and delivery services as two separate labeled sections
 * instead of merging them into one flat list. Bookends (Shipper/Consignee) and a middle stop with
 * only one direction's services still render a single unlabeled section, unchanged from before.
 * Also carries the stop's Address and Company name (mirrors StopServicesTooltipContent.tsx in
 * wal-frontend-v2, which shows both above the services), each rendered whenever the stop has one. */
const StopServicesTooltipContent: FC<{ fullAddress: string; companyName: string; pickupServices: IStopServiceView[]; deliveryServices: IStopServiceView[] }> = ({
    fullAddress,
    companyName,
    pickupServices,
    deliveryServices,
}) => {
    const { t } = useTranslation();
    const hasBoth = pickupServices.length > 0 && deliveryServices.length > 0;

    return (
        <div className="space-y-2 text-left">
            {fullAddress ? (
                <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-grayLight900">{t('Common.Address')}</p>
                    <p className="text-xs text-grayLight600">{fullAddress}</p>
                </div>
            ) : null}
            {companyName ? (
                <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-grayLight900">{t('Common.Company')}</p>
                    <p className="text-xs text-grayLight600">{companyName}</p>
                </div>
            ) : null}
            {hasBoth ? (
                <>
                    <StopServicesSection title={t('QuotingHub.Common.Pickup_Additional_Services')} services={pickupServices} />
                    <StopServicesSection title={t('QuotingHub.Common.Delivery_Additional_Services')} services={deliveryServices} />
                </>
            ) : pickupServices.length > 0 || deliveryServices.length > 0 ? (
                <StopServicesSection title={t('Common.Additional_Services')} services={pickupServices.length > 0 ? pickupServices : deliveryServices} />
            ) : null}
        </div>
    );
};

/** Mirrors StopSummaryStrip.tsx's CountBadge exactly — green up / red down, item-breakdown tooltip only when items exist. */
const CountBadge: FC<{ direction: 'up' | 'down'; count: number; items: IDimension[]; title: string }> = ({ direction, count, items, title }) => {
    const Icon = direction === 'up' ? ArrowUp : ArrowDown;
    const colorClass = direction === 'up' ? 'text-fgSuccessPrimary' : 'text-fgErrorPrimary';

    const badge = (
        <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${colorClass} ${items.length > 0 ? 'cursor-pointer' : ''}`}>
            <Icon className="w-3.5 h-3.5 flex-none" />
            {count}
        </span>
    );

    if (items.length === 0) {
        return badge;
    }

    return (
        <TooltipCmp
            isArrow={false}
            position="bottom"
            childrenClassName="flex min-w-0"
            TooltipCustomClass="tooltipV3-content-wrap"
            isHoverPersistent
            parentClassName="tooltip-content-with-scroll-wrap custom-scrollbar-v2 min-w-[280px]"
            message={<StopItemsTooltipContent title={title} items={items} />}
        >
            {badge}
        </TooltipCmp>
    );
};

/** Shared by the loaded segments and the skeleton segments so the two can never drift apart. */
const SEGMENT_CLASS = 'flex-1 min-w-[220px] px-4 py-2.5 space-y-1 overflow-hidden border-r border-utilityGray200 last:border-r-0';

const CustomerPortalStopMetaStrip: FC<IProps> = ({ stops, summary, dimensions = [], selectedServices = [], isLoading = false, skeletonStopCount = 0 }) => {
    const { t } = useTranslation();
    // Read the service catalog straight from Redux instead of via useAddressDetails() — that hook
    // dispatches authenticated fetches on mount, and this strip also renders on the public
    // share-link page where any 401 clears localStorage and bounces the visitor to the login page.
    // Empty there, which is fine: each stop's own `services` (attached by the backend) is the
    // primary source and the catalog is only a fallback for serviceIds-only stop payloads.
    const services = useSelector((state: RootState) => state.basic.services);

    // Bug fix (user-reported) — stops always includes the shipper/consignee bookends even when no
    // real (customer-requested) middle stops survived the backend's filterCustomerVisibleStops
    // (e.g. a quote created from the WAL side). A plain 2-point route renders like a classic quote
    // — no strip at all — so this needs at least one genuine middle stop, not just "any stops".
    // While loading, the same >2 test runs against the count the caller passed from router state.
    const stopsTotal = isLoading ? skeletonStopCount : stops?.length ?? 0;

    if (stopsTotal <= 2) return null;

    const cards = isLoading ? [] : buildStopCards({ stops, summary, selectedServices, dimensions, services, t });

    // Headings are known from the count alone (bookends + 1..M middles, same numbering
    // buildStopCards uses), so only the value line is skeletoned — mirrors
    // QuoteDetailMetaDataLoaderRow, which also keeps its real headings while the values load.
    const skeletonLabel = (index: number) => (index === 0 ? t('Common.Shipper') : index === stopsTotal - 1 ? t('Common.Consignee') : t('MyQuote.Stop_N', { n: index }));

    return (
        <>
            {/* flex-none: the Overview column is a flex layout — without it this section could be
            squeezed when the content below overflows (mirrors StopSummaryStrip.tsx). */}
            <section className="flex-none w-full rounded-xl border border-utilityGray200 bg-white overflow-x-auto custom-scrollbar-v2 scrollbar-hide shadow-xs" data-testid="stop-meta-strip">
                <div className="flex w-full min-w-max">
                    {isLoading
                        ? Array.from({ length: stopsTotal }).map((_, index) => (
                              <div key={index} className={SEGMENT_CLASS}>
                                  <h6 className="table-metadata-heading">{skeletonLabel(index)}</h6>
                                  <div className="flex items-center justify-between gap-2 min-w-0">
                                      <p className="table-metadata-value-text custom-loading rounded-md truncate min-w-0">Lorem Ipsum</p>
                                      <span className="flex items-center gap-2 flex-none">
                                          <span className="custom-loading rounded-md text-xs font-medium">00</span>
                                          <span className="custom-loading rounded-md text-xs font-medium">00</span>
                                      </span>
                                  </div>
                              </div>
                          ))
                        : null}
                    {cards.map((card) => (
                        <div key={card.position} className={SEGMENT_CLASS}>
                            <h6 className="table-metadata-heading">{card.label}</h6>
                            <div className="flex items-center justify-between gap-2 min-w-0">
                                {card.fullAddress || card.companyName || card.pickupServices.length > 0 || card.deliveryServices.length > 0 ? (
                                    // Stop name doubles as the address/services hover target (mirrors StopSummaryStrip.tsx) —
                                    // opens whenever there's an address to show, not just when the stop has services.
                                    <TooltipCmp
                                        style={{ display: 'flex', minWidth: 0 }}
                                        childrenClassName="flex min-w-0"
                                        isArrow={false}
                                        position="bottom"
                                        isHoverPersistent
                                        parentClassName="tooltip-content-with-scroll-wrap custom-scrollbar-v2 w-[320px] text-left"
                                        message={
                                            <StopServicesTooltipContent
                                                fullAddress={card.fullAddress}
                                                companyName={card.companyName}
                                                pickupServices={card.pickupServices}
                                                deliveryServices={card.deliveryServices}
                                            />
                                        }
                                    >
                                        {/* Dotted underline = "hover for more", same affordance WALHQ's LaneStopOverviewCard uses. */}
                                        <p className="table-metadata-value-text truncate min-w-0 cursor-pointer underline decoration-dotted underline-offset-2">{card.stopName || '-'}</p>
                                    </TooltipCmp>
                                ) : (
                                    <p className="table-metadata-value-text truncate min-w-0 flex-1">{card.stopName || '-'}</p>
                                )}
                                <span className="flex items-center gap-2 flex-none">
                                    <CountBadge direction="up" count={card.unitsOn} items={card.pickupItems} title={t('MyQuote.Pickup_Here')} />
                                    <CountBadge direction="down" count={card.unitsOff} items={card.dropItems} title={t('MyQuote.Drop_Off_Here')} />
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </>
    );
};

export default CustomerPortalStopMetaStrip;
